"use client";

import { useTranslations } from "next-intl";
import { motion } from "motion/react";
import {
    SectionShell,
    EditorialTitle,
    GradientText,
    RevealOnScroll,
} from "@/components/ui/Socials/JoinInstructors/_primitives";

const TOOLS = [
    { id: 1, name: "Calendly", icon: "📅" },
    { id: 2, name: "Stripe", icon: "💳" },
    { id: 3, name: "Zoom", icon: "🎥" },
    { id: 4, name: "Factures", icon: "📄" },
    { id: 5, name: "Relances", icon: "🔔" },
    { id: 6, name: "Remboursements", icon: "↩️" },
];

export default function ProblemSection() {
    const t = useTranslations();

    return (
        <SectionShell label={t("joinMentor_problem_marker")}>
            <RevealOnScroll>
                <p className="text-[11px] uppercase tracking-[0.28em] text-primary mb-6">
                    {t("joinMentor_problem_eyebrow")}
                </p>
            </RevealOnScroll>

            <RevealOnScroll delay={0.05}>
                <EditorialTitle className="text-4xl sm:text-5xl md:text-7xl mb-12 sm:mb-16">
                    {t("joinMentor_problem_title_part1")}
                    <br />
                    {t("joinMentor_problem_title_part2")}{" "}
                    <GradientText>{t("joinMentor_problem_title_emphasis")}</GradientText>
                </EditorialTitle>
            </RevealOnScroll>

            {/* Cluster of scattered tool cards */}
            <RevealOnScroll delay={0.1}>
                <div className="relative max-w-4xl mx-auto h-[280px] sm:h-[320px] mb-12 sm:mb-16">
                    {TOOLS.map((tool, i) => {
                        const positions = [
                            { left: "5%", top: "10%", rot: -8 },
                            { left: "30%", top: "5%", rot: 5 },
                            { left: "60%", top: "15%", rot: -3 },
                            { left: "8%", top: "60%", rot: 7 },
                            { left: "40%", top: "55%", rot: -5 },
                            { left: "70%", top: "65%", rot: 9 },
                        ];
                        const pos = positions[i];
                        return (
                            <motion.div
                                key={tool.id}
                                initial={{ opacity: 0, y: 20, rotate: pos.rot - 4 }}
                                whileInView={{ opacity: 1, y: 0, rotate: pos.rot }}
                                viewport={{ once: true }}
                                transition={{
                                    duration: 0.6,
                                    delay: 0.2 + i * 0.08,
                                    ease: [0.16, 1, 0.3, 1],
                                }}
                                style={{
                                    position: "absolute",
                                    left: pos.left,
                                    top: pos.top,
                                }}
                                className="will-change-transform"
                            >
                                <div className="px-4 py-3 sm:px-5 sm:py-4 rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-white/[0.03] shadow-[0_10px_40px_-15px_rgba(0,0,0,0.2)] flex items-center gap-2.5">
                                    <span className="text-xl sm:text-2xl" aria-hidden="true">
                                        {tool.icon}
                                    </span>
                                    <span className="text-sm sm:text-base font-medium text-gray-700 dark:text-white/85">
                                        {t(`joinMentor_problem_tool${tool.id}`)}
                                    </span>
                                </div>
                            </motion.div>
                        );
                    })}
                </div>
            </RevealOnScroll>

            <RevealOnScroll delay={0.4}>
                <div className="pt-6 sm:pt-8 border-t border-gray-200 dark:border-white/10 flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
                    <p
                        className="text-gray-500 dark:text-white/55 italic text-base sm:text-lg max-w-2xl"
                        style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                    >
                        {t("joinMentor_problem_footer_part1")}
                    </p>
                    <p className="text-primary font-semibold whitespace-nowrap">
                        {t("joinMentor_problem_footer_part2")}
                    </p>
                </div>
            </RevealOnScroll>
        </SectionShell>
    );
}
